import React, { useState, useEffect } from 'react';
import AccountingService from '../../services/AccountingService';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';

const ChartOfAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); // 'add' or 'edit'
  const [currentAccount, setCurrentAccount] = useState({
    accountCode: '',
    accountName: '',
    accountType: '',
    description: '',
    isActive: true
  });
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const accountTypes = ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'];

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await AccountingService.getAllAccounts();
      // Ensure we have an array
      const data = Array.isArray(response.data) ? response.data : 
                   (response.data && Array.isArray(response.data.data)) ? response.data.data : [];
      setAccounts(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching accounts:', err);
      setError('Failed to load accounts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (mode, account = null) => {
    setDialogMode(mode);
    if (account) {
      setCurrentAccount({
        accountId: account.accountId,
        accountCode: account.accountCode,
        accountName: account.accountName,
        accountType: account.accountType,
        description: account.description || '',
        isActive: account.isActive
      });
    } else {
      setCurrentAccount({
        accountCode: '',
        accountName: '',
        accountType: '',
        description: '',
        isActive: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentAccount({
      ...currentAccount,
      [name]: value
    });
  };

  const handleSaveAccount = async () => {
    try {
      if (dialogMode === 'add') {
        await AccountingService.createAccount(currentAccount);
        showSnackbar('Account created successfully', 'success');
      } else {
        await AccountingService.updateAccount(currentAccount.accountId, currentAccount);
        showSnackbar('Account updated successfully', 'success');
      }
      handleCloseDialog();
      fetchAccounts();
    } catch (err) {
      console.error('Error saving account:', err);
      showSnackbar('Failed to save account', 'error');
    }
  };

  const handleDeleteAccount = async (id) => {
    if (window.confirm('Are you sure you want to delete this account?')) {
      try {
        await AccountingService.deleteAccount(id);
        showSnackbar('Account deleted successfully', 'success');
        fetchAccounts();
      } catch (err) {
        console.error('Error deleting account:', err);
        showSnackbar('Failed to delete account', 'error');
      }
    }
  };

  const handleSeedDefaults = async () => {
    try {
      setLoading(true);
      await AccountingService.seedDefaultAccounts();
      showSnackbar('Default accounts seeded successfully', 'success');
      fetchAccounts();
    } catch (err) {
      console.error('Error seeding defaults:', err);
      showSnackbar('Failed to seed default accounts', 'error');
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center' }}>
          <AccountBalanceIcon sx={{ mr: 2 }} />
          Chart of Accounts
        </Typography>
        <Box>
          {accounts.length === 0 && !loading && (
            <Button 
              variant="outlined" 
              color="secondary" 
              onClick={handleSeedDefaults}
              sx={{ mr: 2 }}
            >
              Seed Defaults
            </Button>
          )}
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('add')}
          >
            New Account
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accounts.length > 0 ? (
                    accounts.map((account) => (
                      <TableRow key={account.accountId}>
                        <TableCell>{account.accountCode}</TableCell>
                        <TableCell>{account.accountName}</TableCell>
                        <TableCell>
                          <Chip 
                            label={account.accountType} 
                            color={
                              account.accountType === 'Asset' || account.accountType === 'Expense' 
                                ? 'primary' 
                                : 'secondary'
                            } 
                            size="small" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{account.description}</TableCell>
                        <TableCell>
                          <Chip 
                            label={account.isActive ? 'Active' : 'Inactive'} 
                            color={account.isActive ? 'success' : 'default'} 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton 
                            color="primary" 
                            onClick={() => handleOpenDialog('edit', account)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            color="error" 
                            onClick={() => handleDeleteAccount(account.accountId)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No accounts found. Create one or seed defaults.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Account Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{dialogMode === 'add' ? 'Add New Account' : 'Edit Account'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              name="accountCode"
              label="Account Code"
              value={currentAccount.accountCode}
              onChange={handleInputChange}
              fullWidth
              required
              helperText="e.g. 1000, 4000"
            />
            <TextField
              name="accountName"
              label="Account Name"
              value={currentAccount.accountName}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <FormControl fullWidth required>
              <InputLabel>Account Type</InputLabel>
              <Select
                name="accountType"
                value={currentAccount.accountType}
                label="Account Type"
                onChange={handleInputChange}
              >
                {accountTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              name="description"
              label="Description"
              value={currentAccount.description}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={3}
            />
            {dialogMode === 'edit' && (
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="isActive"
                  value={currentAccount.isActive}
                  label="Status"
                  onChange={(e) => setCurrentAccount({...currentAccount, isActive: e.target.value})}
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveAccount} 
            variant="contained" 
            disabled={!currentAccount.accountCode || !currentAccount.accountName || !currentAccount.accountType}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChartOfAccounts;
